import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { AlertCircle } from "lucide-react";

import { createAxiosInstance } from "api/axiosConfig";
import { SafeHtml } from "components/SafeHtml/SafeHtml";
import { Dialog } from "components/ui/Dialog";
import { Pill, PILL_COLORS } from "components/ui/Pill";
import { useEmailDetail } from "features/communications/hooks/useEmailDetail";
import { usePatientName } from "features/communications/hooks/usePatientName";
import { pathGenerators } from "utils/routes";

import styles from "./EmailDetailModal.module.css";

const STATUS_PILL = {
  SENT: PILL_COLORS.statusSent,
  PENDING: PILL_COLORS.statusPending,
  FAILED: PILL_COLORS.statusError,
};

const formatStatus = (status) => {
  if (!status) return "-";
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

const formatDateTime = (value) => {
  if (!value) return "-";
  try {
    return format(new Date(value), "dd MMM yyyy, HH:mm");
  } catch {
    return value;
  }
};

const resolveSenderName = (sentBy, createdBy) => {
  if (sentBy?.firstName || sentBy?.lastName) {
    return `${sentBy.firstName || ""} ${sentBy.lastName || ""}`.trim();
  }
  return createdBy || null;
};

const useDocumentById = (documentId) => {
  const tenantVal = localStorage.getItem("kinesin-tenant");

  return useQuery({
    queryKey: ["document", tenantVal, documentId],
    queryFn: async () => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(
        `${tenantVal}/document/get/${documentId}`
      );
      return response.data;
    },
    enabled: !!tenantVal && !!documentId,
  });
};

const DocumentAttachmentItem = ({ documentId, patientId }) => {
  const tenantVal = localStorage.getItem("kinesin-tenant");
  const { data: doc } = useDocumentById(documentId);

  const handleDownload = async () => {
    try {
      const axiosInstance = createAxiosInstance();
      const patientPart = patientId ? `/patient/${patientId}` : "";
      const response = await axiosInstance.get(
        `${tenantVal}/document/download/${documentId}${patientPart}`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = doc?.name ? `${doc.name}.pdf` : `document-${documentId}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      // silently fail — download errors are non-critical
    }
  };

  return (
    <button className={styles.attachmentItem} onClick={handleDownload}>
      <span className={styles.attachmentIcon}>📎</span>
      <div>
        <div className={styles.attachmentName}>
          {doc?.name || `Document #${documentId}`}
        </div>
        <div className={styles.attachmentType}>
          {doc?.documentType || "Document"}
        </div>
      </div>
    </button>
  );
};

const PatientLink = ({ patientId }) => {
  const navigate = useNavigate();
  const { data: patient } = usePatientName(patientId);

  if (!patientId) return <span>—</span>;

  const name = patient
    ? `${patient.firstName || ""} ${patient.surname || ""}`.trim()
    : `#${patientId}`;

  // Patient id travels in the path (/patients/:patientId/communication).
  return (
    <button
      className={styles.patientLink}
      onClick={() => navigate(pathGenerators.patient.communication(patientId))}
    >
      {name}
    </button>
  );
};

export const EmailDetailModal = ({ emailId, onClose }) => {
  const tenantVal = localStorage.getItem("kinesin-tenant");
  const { data: email, isLoading } = useEmailDetail(emailId);

  // documentIds arrives as a bracketed/stringified value (e.g. "[1018]" or
  // "[1018, 1019]"), so extract the bare numeric ids — splitting on comma alone
  // would keep the brackets and produce a malformed "document/get/[1018]" URL.
  const documentIds = email?.documentIds
    ? String(email.documentIds).match(/\d+/g) ?? []
    : [];

  const statusKey = email?.status?.toUpperCase();
  const senderName = resolveSenderName(email?.sentBy, email?.createdBy);

  return (
    <Dialog
      open={!!emailId}
      onClose={onClose}
      title={isLoading ? "Loading…" : email?.subject || "Email Detail"}
      width={900}
    >
      {isLoading && <p className={styles.loading}>Loading email…</p>}

      {!isLoading && !!email && (
        <div className={styles.content}>
          <div className={styles.patientCard}>
            {!!email.patientId && (
              <div className={styles.patientRow}>
                <span className={styles.patientLabel}>Patient: </span>
                <PatientLink patientId={email.patientId} />
              </div>
            )}

            <div className={styles.patientRow}>
              <span className={styles.patientLabel}>To: </span>
              {email.emailAddress || "—"}
            </div>
          </div>

          <div className={styles.fromSubjectGrid}>
            <div className={styles.fieldInline}>
              <span className={styles.fieldLabel}>Subject</span>
              <div className={styles.readValue}>{email.subject || "—"}</div>
            </div>

            <div className={styles.fieldInline}>
              <span className={styles.fieldLabel}>From</span>
              <div className={styles.readValue}>
                {!!senderName && (
                  <span className={styles.senderName}>{senderName}</span>
                )}
                {!!email.senderEmailAddress && (
                  <span className={styles.senderEmail}>
                    {senderName
                      ? ` <${email.senderEmailAddress}>`
                      : email.senderEmailAddress}
                  </span>
                )}
                {!senderName && !email.senderEmailAddress && "—"}
              </div>
            </div>
          </div>

          <div className={styles.statusRow}>
            <Pill {...(STATUS_PILL[statusKey] ?? PILL_COLORS.statusNoStatus)}>
              {formatStatus(email.status)}
            </Pill>
            {!!(email.sentAt || email.created) && (
              <span className={styles.sentAt}>
                {formatDateTime(email.sentAt || email.created)}
              </span>
            )}
          </div>

          {email.status === "FAILED" && !!email.errorMessage && (
            <div className={styles.errorBanner}>
              <AlertCircle size={15} />
              <span>{email.errorMessage}</span>
            </div>
          )}

          <div className={styles.field}>
            <span className={styles.fieldLabel}>Message</span>
            <SafeHtml html={email.body} className={styles.body} />
          </div>

          {documentIds.length > 0 && (
            <div className={styles.field}>
              <span className={styles.fieldLabel}>
                Attachments ({documentIds.length})
              </span>
              <div className={styles.attachments}>
                {documentIds.map((documentId) => (
                  <DocumentAttachmentItem
                    key={documentId}
                    documentId={documentId}
                    patientId={email.patientId}
                    tenantVal={tenantVal}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
};
